/** One success case on the asset's Success cases tab, with the actions the caller may take on it. */

import type { ReactNode } from 'react';
import { Globe, Link2Off, Lock, Pencil } from 'lucide-react';

import type { CaseOut } from '@/api/types/cases';
import { useSession } from '@/session/SessionContext';

import { CaseBody } from './CaseBody';
import { CaseStatusBadge, CaseTypeBadge, ShareBadge } from './badges';

export type CaseAction = 'edit' | 'share' | 'internal' | 'unlink';

function Meta({ item }: { item: CaseOut }) {
  return (
    <div className="case-meta">
      <CaseTypeBadge type={item.type} />
      <CaseStatusBadge status={item.status} />
      <ShareBadge shareable={item.shareable} />
      <span>{item.industry?.name ?? 'No industry'}</span>
      <span>{item.country ?? 'No country'}</span>
    </div>
  );
}

function Actions({ item, onAction }: { item: CaseOut; onAction: (a: CaseAction) => void }) {
  const { can } = useSession();
  if (!can('asset.manage')) return null;
  const internal = can('asset.view.internal');
  const button = (action: CaseAction, text: string, icon: ReactNode) => (
    <button type="button" className="btn btn-secondary btn-sm" aria-label={`${text}: ${item.label}`} onClick={() => onAction(action)}>
      {icon} {text}
    </button>
  );
  return (
    <div className="case-actions">
      {internal && button('edit', 'Edit', <Pencil aria-hidden="true" />)}
      {internal && !item.shareable && button('share', 'Share with clients', <Globe aria-hidden="true" />)}
      {item.shareable && button('internal', 'Make internal', <Lock aria-hidden="true" />)}
      {button('unlink', 'Unlink', <Link2Off aria-hidden="true" />)}
    </div>
  );
}

export function CaseCard({ item, onAction }: { item: CaseOut; onAction: (action: CaseAction) => void }) {
  return (
    <article className="card case-card" aria-labelledby={`case-${item.id}`}>
      <div className="card-body">
        <h3 id={`case-${item.id}`}>{item.label}</h3>
        {item.view === 'named' && item.label !== item.anonymized_label && (
          <span className="hint">Shown to clients as {item.anonymized_label}</span>
        )}
        <Meta item={item} />
        <CaseBody content={item} label={item.label} />
        <Actions item={item} onAction={onAction} />
      </div>
    </article>
  );
}
